/**
 * Feature Flag Management Utility
 *
 * This utility provides a centralized way to manage feature flags in the application.
 * Feature flags can be controlled via the browser logger for testing and gradual rollouts.
 *
 * Usage in browser logger:
 * - Enable a feature: window.featureFlags.enable('DEMO_HISTORY_PLAYS')
 * - Disable a feature: window.featureFlags.disable('DEMO_HISTORY_PLAYS')
 * - Check if feature is enabled: window.featureFlags.isEnabled('DEMO_HISTORY_PLAYS')
 * - List all features: window.featureFlags.list()
 * - Reset all features: window.featureFlags.reset()
 *
 * Global Settings:
 * - FORCE_DEFAULT_FEATURE_FLAGS: When set to true, all users will use the default feature flag
 *   settings, ignoring any localStorage values. This effectively resets feature flags for all users.
 */

import { logger } from "./LogUtils";
import { useEffect } from "react";
import { useAuth, User } from "../components/Authentication";
import { isPartnerUser } from "./AuthUtils";

export enum FeatureFlag {
  DEMO_HISTORY_PLAYS = "DEMO_HISTORY_PLAYS",
}

// Set this to true to force all users to use default feature flag settings
// This will clear localStorage settings for all users when they load the application
export const FORCE_DEFAULT_FEATURE_FLAGS = false;

const DEFAULT_FEATURE_FLAGS: Record<FeatureFlag, boolean> = {
  [FeatureFlag.DEMO_HISTORY_PLAYS]: true,
};

const STORAGE_KEY = "demo_portal_feature_flags";

class FeatureFlagManager {
  private flags: Record<string, boolean> = {};
  private currentUser: User | null = null;

  constructor() {
    this.loadFromStorage();
    this.exposeToWindow();
  }

  /**
   * Set the current user for partner checks
   * This should be called whenever the auth state changes
   */
  public setCurrentUser(user: User | null): void {
    this.currentUser = user;
    logger.log("Feature flag manager: User updated", user?.email);
  }

  public getCurrentUser(): User | null {
    return this.currentUser;
  }

  private loadFromStorage(): void {
    try {
      if (FORCE_DEFAULT_FEATURE_FLAGS) {
        localStorage.removeItem(STORAGE_KEY);
        this.flags = { ...DEFAULT_FEATURE_FLAGS };
        logger.log(
          "🔄 Feature flags forced to default values due to FORCE_DEFAULT_FEATURE_FLAGS setting"
        );
        return;
      }

      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsedFlags = JSON.parse(stored);
        this.flags = { ...DEFAULT_FEATURE_FLAGS, ...parsedFlags };
      } else {
        this.flags = { ...DEFAULT_FEATURE_FLAGS };
      }
    } catch (error) {
      logger.warn("Failed to load feature flags from storage:", error);
      this.flags = { ...DEFAULT_FEATURE_FLAGS };
    }
  }

  private saveToStorage(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.flags));
    } catch (error) {
      logger.warn("Failed to save feature flags to storage:", error);
    }
  }

  private exposeToWindow(): void {
    if (typeof window !== "undefined" && !this.isPartnerUser()) {
      (window as any).featureFlags = {
        enable: (flag: string) => {
          return this.enable(flag);
        },
        disable: (flag: string) => {
          return this.disable(flag);
        },
        isEnabled: (flag: string) => this.isEnabled(flag),
        list: () => this.list(),
        reset: () => {
          return this.reset();
        },
        forceReset: () => {
          return this.forceReset();
        },
        help: () => this.help(),
      };
    }
  }

  public isPartnerUser(): boolean {
    if (this.currentUser !== null && this.currentUser !== undefined) {
      return this.currentUser.partner === true;
    }
    return false;
  }

  public isEnabled(flag: FeatureFlag | string): boolean {
    if (this.isPartnerUser()) {
      return false;
    }

    return this.flags[flag] === true;
  }

  public enable(flag: FeatureFlag | string): boolean {
    if (this.isPartnerUser()) {
      return false;
    }

    this.flags[flag] = true;
    this.saveToStorage();
    logger.log(`✅ Feature flag '${flag}' enabled`);
    return true;
  }

  public disable(flag: FeatureFlag | string): boolean {
    if (this.isPartnerUser()) {
      return false;
    }

    this.flags[flag] = false;
    this.saveToStorage();
    logger.log(`❌ Feature flag '${flag}' disabled`);
    return true;
  }

  public list(): Record<string, boolean> {
    if (this.isPartnerUser()) {
      return {};
    }

    logger.table(this.flags);
    return this.flags;
  }

  public reset(): boolean {
    if (this.isPartnerUser()) {
      return false;
    }

    this.flags = { ...DEFAULT_FEATURE_FLAGS };
    this.saveToStorage();
    logger.log("🔄 All feature flags reset to default values");
    logger.table(this.flags);
    return true;
  }

  public forceReset(): boolean {
    if (this.isPartnerUser()) {
      return false;
    }

    localStorage.removeItem(STORAGE_KEY);
    this.flags = { ...DEFAULT_FEATURE_FLAGS };
    logger.log(
      "🔄 All feature flags force reset to default values and localStorage cleared"
    );
    logger.table(this.flags);
    return true;
  }

  public help(): void {
    if (!this.currentUser || this.isPartnerUser()) return;
    logger.log(`
🚩 Feature Flag Management

Available commands:
• window.featureFlags.enable('FEATURE_NAME') - Enable a feature
• window.featureFlags.disable('FEATURE_NAME') - Disable a feature  
• window.featureFlags.isEnabled('FEATURE_NAME') - Check if feature is enabled
• window.featureFlags.list() - Show all feature flags
• window.featureFlags.reset() - Reset all flags to defaults
• window.featureFlags.forceReset() - Clear localStorage and reset to defaults
• window.featureFlags.help() - Show this help

Available Features:
${Object.keys(DEFAULT_FEATURE_FLAGS)
  .map((flag) => `• ${flag}`)
  .join("\n")}

Example:
window.featureFlags.enable('DEMO_HISTORY_PLAYS')
    `);
  }

  public getAllFlags(): Record<string, boolean> {
    if (this.isPartnerUser()) {
      return {};
    }
    return { ...this.flags };
  }
}

// Create singleton instance
export const featureFlagManager = new FeatureFlagManager();

/**
 * Hook-like function to check if a feature is enabled
 * Can be used in React components
 */
export const useFeatureFlag = (flag: FeatureFlag): boolean => {
  return featureFlagManager.isEnabled(flag);
};

/**
 * Simple function to check if a feature is enabled
 * Can be used anywhere in the application
 */
export const isFeatureEnabled = (flag: FeatureFlag): boolean => {
  return featureFlagManager.isEnabled(flag);
};

/**
 * Hook to sync the current user from useAuth() with the feature flag manager
 * This should be used in a high-level component that has access to the auth context
 */
export const useSyncAuthWithFeatureFlags = (): void => {
  const { user } = useAuth();

  useEffect(() => {
    // Only update when user is defined (after authentication is complete)
    if (user !== undefined && !isPartnerUser(user)) {
      featureFlagManager.setCurrentUser(user);
    }
  }, [user]);
};

// Initialize the feature flag manager
featureFlagManager.help();
